import type { Text } from '@codemirror/state';
import type { IntlShape } from 'react-intl';

import type { InlineDiagnostic } from '../../code-editor/inline-diagnostic.js';
import { BASE_CODE_EDITOR_MESSAGES } from '../baseCodeEditorMessages.js';

/**
 * Formats a summary of the given diagnostics for screen reader announcements.
 * @param diagnostics - The diagnostics to summarize.
 * @param intl - The `react-intl` instance to use for formatting messages.
 * @param doc - The CodeMirror document to use for calculating line numbers.
 * @returns A string summarizing the diagnostics, suitable for screen reader announcements.
 * @internal
 */
export function formatDiagnosticsSummary(
  diagnostics: InlineDiagnostic[],
  intl: IntlShape,
  doc: Text,
): string {
  const lineNumberFor = (offset: number) =>
    doc.lineAt(Math.max(0, Math.min(offset, doc.length))).number;

  const severityMessages = {
    error: BASE_CODE_EDITOR_MESSAGES.diagnosticsSeverityError,
    warning: BASE_CODE_EDITOR_MESSAGES.diagnosticsSeverityWarning,
    info: BASE_CODE_EDITOR_MESSAGES.diagnosticsSeverityInfo,
  } as const;

  const formatSeverity = (d: InlineDiagnostic) =>
    intl.formatMessage(severityMessages[d.severity ?? 'error']);

  if (diagnostics.length === 1) {
    const [d] = diagnostics;
    return intl.formatMessage(
      BASE_CODE_EDITOR_MESSAGES.diagnosticsSummarySingle,
      {
        severity: formatSeverity(d),
        line: lineNumberFor(d.from),
        message: d.message,
      },
    );
  }

  const list = diagnostics
    .map((d) =>
      intl.formatMessage(BASE_CODE_EDITOR_MESSAGES.diagnosticsSummaryListItem, {
        severity: formatSeverity(d),
        line: lineNumberFor(d.from),
        message: d.message,
      }),
    )
    .join('. ');

  return intl.formatMessage(
    BASE_CODE_EDITOR_MESSAGES.diagnosticsSummaryMultiple,
    {
      count: diagnostics.length,
      list,
    },
  );
}
